import { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { api, API, apiErr } from "../lib/api";
import { fmt } from "../lib/format";
import ProximityOffers from "../components/ProximityOffers";
import { NotificationPrompt } from "../components/NotificationPrompt";
import { Loader2, Check, Clock, ChefHat, PackageCheck, X, Truck, Hourglass } from "lucide-react";
import { RiderMap } from "../components/RiderMap";

function haversineKm(a, b) {
  const R = 6371, toR = (d) => (d * Math.PI) / 180;
  const dLat = toR(b[0] - a[0]), dLon = toR(b[1] - a[1]);
  const x = Math.sin(dLat / 2) ** 2 + Math.cos(toR(a[0])) * Math.cos(toR(b[0])) * Math.sin(dLon / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(x));
}

const STATUS_MSG = {
  accepted: "Il tuo ordine è stato accettato",
  preparing: "Il tuo ordine è in preparazione",
  ready: "Il tuo ordine è pronto! 🎉",
  out_for_delivery: "Il tuo ordine è in consegna 🛵",
  delivered: "Ordine consegnato. Buon appetito!",
  completed: "Ordine completato. Buon appetito!",
  rejected: "Il tuo ordine è stato rifiutato",
  cancelled: "Il tuo ordine è stato annullato",
};

const STEPS = [
  { key: "new", label: "Ordine ricevuto", icon: Check },
  { key: "accepted", label: "Accettato", icon: Check },
  { key: "preparing", label: "In preparazione", icon: ChefHat },
  { key: "ready", label: "Pronto per il ritiro", icon: PackageCheck },
  { key: "completed", label: "Completato", icon: Check },
];
const ORDER = ["new", "accepted", "preparing", "ready", "completed"];
const STEPS_DELIVERY = [
  { key: "new", label: "Ordine ricevuto", icon: Check },
  { key: "accepted", label: "Accettato", icon: Check },
  { key: "preparing", label: "In preparazione", icon: ChefHat },
  { key: "ready", label: "Pronto", icon: PackageCheck },
  { key: "out_for_delivery", label: "In consegna", icon: Truck },
  { key: "delivered", label: "Consegnato", icon: Check },
];
const ORDER_DELIVERY = ["new", "accepted", "preparing", "ready", "out_for_delivery", "delivered"];

export default function OrderTracking() {
  const { token } = useParams();
  const [order, setOrder] = useState(null);
  const [err, setErr] = useState(null);
  const [live, setLive] = useState(true);
  const esRef = useRef(null);
  const lastStatusRef = useRef(null);
  const orderRef = useRef(null);
  const redirectedRef = useRef(false);
  const expiredRef = useRef(false);
  const [nowTs, setNowTs] = useState(Date.now());
  const [riderPos, setRiderPos] = useState(null);
  const [riderRoute, setRiderRoute] = useState(null);

  const notify = (status) => {
    const msg = STATUS_MSG[status] || "Aggiornamento sul tuo ordine";
    const title = `Ordine #${orderRef.current?.short_code || ""}`;
    toast(title, { description: msg });
    try {
      if (typeof Notification !== "undefined" && Notification.permission === "granted") {
        new Notification(title, { body: msg, icon: "/pickfood-icon.svg" });
      }
    } catch (e) { console.debug("system notification unavailable", e); }
  };

  useEffect(() => {
    let alive = true;
    const load = () => api.get(`/store/order/${token}`).then((r) => {
      if (!alive) return;
      setOrder(r.data); orderRef.current = r.data;
      if (r.data.rider_location) setRiderPos([r.data.rider_location.lat, r.data.rider_location.lng]);
      if (r.data.rider_route) setRiderRoute(r.data.rider_route);
      if (lastStatusRef.current === null) lastStatusRef.current = r.data.op_status;
      if (r.data.op_status === "confirmed" && r.data.pay_status === "pending" && !r.data.revised && !redirectedRef.current) {
        redirectedRef.current = true;
        window.location.assign(`/pay/${token}`);
      }
    }).catch((e) => setErr(apiErr(e)));
    load();
    const es = new EventSource(`${API}/stream/order/${token}`);
    esRef.current = es;
    es.onmessage = (ev) => {
      try {
        const d = JSON.parse(ev.data);
        if (d.type === "status") {
          load();
          if (d.op_status && d.op_status !== lastStatusRef.current) {
            lastStatusRef.current = d.op_status;
            notify(d.op_status);
          }
        } else if (d.type === "rider") {
          setRiderPos([d.lat, d.lng]);
          if (d.route) setRiderRoute(d.route);
        }
      } catch (e) { console.debug("skipped malformed SSE frame", e); }
    };
    es.onopen = () => setLive(true);
    es.onerror = () => setLive(false); // browser auto-reconnects
    return () => { alive = false; es.close(); };
  }, [token]);

  useEffect(() => {
    const t = setInterval(() => setNowTs(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    if (!order || order.op_status !== "new" || order.pay_status !== "pending") { expiredRef.current = false; return; }
    const createdMs = Date.parse(order.created_at);
    const windowMs = (order.confirm_window_seconds || 420) * 1000;
    if (nowTs >= createdMs + windowMs && !expiredRef.current) {
      expiredRef.current = true;
      api.get(`/store/order/${token}`).then((r) => setOrder(r.data)).catch(() => {});
    }
  }, [order, nowTs, token]);

  if (err) return <div className="min-h-screen flex items-center justify-center text-muted-foreground">{err}</div>;
  if (!order) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="animate-spin brand-text" /></div>;

  const rejected = order.op_status === "rejected" || order.op_status === "cancelled";
  const isDel = order.order_type === "delivery";
  const steps = isDel ? STEPS_DELIVERY : STEPS;
  const flow = isDel ? ORDER_DELIVERY : ORDER;
  const curIdx = flow.indexOf(order.op_status);

  const respondAccept = async () => {
    try {
      const { data } = await api.post(`/store/order/${token}/respond`, { accept: true });
      if (data.payment_redirect) window.location.assign(data.payment_redirect);
      else window.location.reload();
    } catch (e) { toast.error(apiErr(e)); }
  };
  const respondReject = async () => {
    try { await api.post(`/store/order/${token}/respond`, { accept: false }); window.location.reload(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const createdMs = order.created_at ? Date.parse(order.created_at) : nowTs;
  const windowMs = (order.confirm_window_seconds || 420) * 1000;
  const remaining = Math.max(0, Math.ceil((createdMs + windowMs - nowTs) / 1000));
  const mm = String(Math.floor(remaining / 60)).padStart(2, "0");
  const ss = String(remaining % 60).padStart(2, "0");

  return (
    <div className="min-h-screen bg-[hsl(var(--secondary))]" style={{ "--brand": "#FFC400" }}>
      <div className="max-w-lg mx-auto px-4 pt-14 md:pt-8 pb-8">
        <div className="bg-card rounded-2xl border p-6 text-center">
          <p className="text-sm text-muted-foreground">Il tuo ordine</p>
          <p className="font-head text-5xl font-extrabold brand-text my-1">#{order.short_code}</p>
          <span className={`inline-block text-xs font-semibold px-3 py-1 rounded-full ${order.pay_status === "paid" ? "bg-[hsl(var(--success))] text-white" : "bg-muted"}`}>
            {order.pay_status === "paid" ? "Pagato online" : order.pay_status === "not_required" ? "Paga al ritiro" : "In attesa di pagamento"}
          </span>
          <p className="text-sm text-muted-foreground mt-3">{isDel ? "Consegna" : "Ritiro"}: {order.pickup_confirmed ? fmt.dt(order.pickup_confirmed) : order.pickup_mode === "first" ? "Prima disponibilità" : fmt.dt(order.pickup_at)}</p>
          {isDel && order.delivery && <p className="text-sm text-muted-foreground">{[order.delivery.address, order.delivery.cap].filter(Boolean).join(" · ")}</p>}
        </div>

        {order.op_status === "new" && (
          <div className="bg-card rounded-2xl border-2 border-[#FFC400] p-6 mt-4 text-center" data-testid="await-confirm-panel">
            <Hourglass className="mx-auto text-[#c79400] mb-2 animate-pulse" size={34} />
            <p className="font-semibold">In attesa di conferma da <span className="text-[#0B0B0D]" data-testid="await-store-name">{order.store_name || "il locale"}</span></p>
            <p className="text-sm text-muted-foreground mt-1">Il locale sta ricevendo il tuo ordine. Appena confermato potrai completare il pagamento.</p>
            {order.pay_status === "pending" ? (
              <>
                <div className="mt-4 inline-flex items-baseline gap-2 font-head font-extrabold text-4xl brand-text tabular-nums" data-testid="await-countdown" style={{ "--brand": "#c79400" }}>
                  {mm}:{ss}
                </div>
                <p className="text-xs text-muted-foreground mt-2">
                  {remaining > 0 ? "Se il locale non conferma entro il tempo, l'ordine verrà annullato automaticamente." : "Tempo scaduto, annullamento in corso…"}
                </p>
              </>
            ) : (
              <p className="text-xs text-muted-foreground mt-3">Riceverai una notifica appena il locale conferma l'ordine.</p>
            )}
          </div>
        )}

        {order.op_status === "confirmed" && order.revised && (
          <div className="bg-card rounded-2xl border-2 border-[#FFC400] p-5 mt-4" data-testid="confirm-panel">
            <p className="font-semibold">Il locale ha modificato il tuo ordine</p>
            <p className="text-sm text-muted-foreground mt-1">Controlla il riepilogo qui sotto, poi accetta per procedere al pagamento.</p>
            <div className="flex gap-2 mt-4">
              <button onClick={respondAccept} data-testid="accept-pay" className="flex-1 h-11 brand-bg text-white rounded-full font-semibold hover:opacity-90">Accetta e paga {fmt.eur(order.total)}</button>
              <button onClick={respondReject} data-testid="reject-order" className="h-11 px-4 rounded-full border text-destructive font-semibold hover:bg-muted">Rifiuta</button>
            </div>
          </div>
        )}

        {order.op_status === "confirmed" && !order.revised && (
          <div className="bg-card rounded-2xl border p-5 mt-4 text-center" data-testid="confirm-redirect-panel">
            <Loader2 className="mx-auto animate-spin brand-text mb-2" size={28} style={{ "--brand": "#c79400" }} />
            <p className="font-semibold">Ordine confermato! Apertura pagamento…</p>
            <button onClick={respondAccept} data-testid="accept-pay" className="mt-3 h-11 px-6 brand-bg text-white rounded-full font-semibold hover:opacity-90" style={{ "--brand": "#FFC400" }}>Vai al pagamento {fmt.eur(order.total)}</button>
          </div>
        )}

        {rejected ? (
          <div className="bg-card rounded-2xl border p-6 mt-4 text-center">
            <X className="mx-auto text-destructive mb-2" size={36} />
            <p className="font-semibold">Ordine {order.op_status === "rejected" ? "rifiutato" : "annullato"}</p>
            {(order.reject_reason || order.cancel_reason) && <p className="text-sm text-muted-foreground mt-1">{order.reject_reason || order.cancel_reason}</p>}
          </div>
        ) : (
          <div className="bg-card rounded-2xl border p-6 mt-4">
            <div className="space-y-1">
              {steps.map((s, i) => {
                const done = curIdx >= i && curIdx >= 0;
                const activeStep = curIdx === i;
                return (
                  <div key={s.key} className="flex items-center gap-3" data-testid={`track-${s.key}`}>
                    <div className={`w-9 h-9 rounded-full flex items-center justify-center ${done ? "brand-bg" : "bg-muted text-muted-foreground"} ${activeStep ? "alert-pulse" : ""}`}>
                      <s.icon size={18} />
                    </div>
                    <span className={`text-sm ${done ? "font-semibold" : "text-muted-foreground"}`}>{s.label}</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {order.op_status === "out_for_delivery" && (order.delivery_latlng || riderPos) && (
          <div className="bg-card rounded-2xl border p-4 mt-4" data-testid="rider-track-panel">
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-semibold flex items-center gap-2"><Truck size={16} className="brand-text" /> Il rider è in arrivo</h3>
              {riderPos && (riderRoute || order.delivery_latlng) && (() => {
                let km, min;
                if (riderRoute && riderRoute.eta_min) { km = riderRoute.distance_km; min = riderRoute.eta_min; }
                else { km = haversineKm(riderPos, [order.delivery_latlng.lat, order.delivery_latlng.lng]); min = Math.max(1, Math.round((km / 18) * 60)); }
                return <span className="text-sm font-semibold brand-text" data-testid="rider-eta">{Number(km).toFixed(1)} km · ~{min} min</span>;
              })()}
            </div>
            <RiderMap
              store={order.store_latlng ? [order.store_latlng.lat, order.store_latlng.lng] : null}
              dest={order.delivery_latlng ? [order.delivery_latlng.lat, order.delivery_latlng.lng] : null}
              rider={riderPos} route={riderRoute?.geometry} />
            {!riderPos && <p className="text-xs text-muted-foreground mt-2">In attesa della posizione del rider…</p>}
          </div>
        )}

        <NotificationPrompt orderToken={token} />
        <div className="bg-card rounded-2xl border p-5 mt-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold">Riepilogo</h3>
            {order.revised && <span className="text-[11px] font-bold text-[#b45309] bg-[#FFC400]/20 border border-[#FFC400]/50 rounded-full px-2 py-0.5" data-testid="ot-revised-badge">Aggiornato dal locale</span>}
          </div>
          {order.items.map((it, i) => {
            const changed = order.revised && it.changed;
            return (
              <div key={i} className={`flex justify-between items-start text-sm py-1.5 px-2 -mx-2 rounded-lg ${changed ? "bg-[#FFC400]/15 border border-[#FFC400]/50" : ""}`} data-testid={`ot-item-${i}`}>
                <span className="pr-2">
                  <span className="font-semibold">{it.quantity}x</span> {it.name}
                  {changed && (
                    <span className="ml-2 inline-flex items-center gap-1 text-[11px] font-bold text-[#b45309]" data-testid={`ot-item-change-${i}`}>
                      {it.prev_quantity == null ? "· Aggiunto" : `· da ${it.prev_quantity}x a ${it.quantity}x`}
                    </span>
                  )}
                </span>
                <span className="whitespace-nowrap">{fmt.eur(it.line_total)}</span>
              </div>
            );
          })}
          {order.revised && (order.removed_items || []).map((it, i) => (
            <div key={`r-${i}`} className="flex justify-between items-center text-sm py-1.5 px-2 -mx-2 rounded-lg bg-red-500/10 border border-red-500/30" data-testid={`ot-removed-${i}`}>
              <span className="line-through text-muted-foreground">{it.quantity}x {it.name}</span>
              <span className="text-[11px] font-bold text-red-600">Rimosso</span>
            </div>
          ))}
          {order.delivery_fee > 0 && <div className="flex justify-between text-sm py-1"><span>Consegna</span><span>{fmt.eur(order.delivery_fee)}</span></div>}
          {order.discount > 0 && <div className="flex justify-between text-sm py-1 text-[hsl(var(--success))]"><span>Sconto {order.coupon?.code}</span><span>-{fmt.eur(order.discount)}</span></div>}
          {order.loyalty_redeemed && order.loyalty_redeemed.amount > 0 && <div className="flex justify-between text-sm py-1 text-[hsl(var(--success))]"><span>Punti fedeltà</span><span>-{fmt.eur(order.loyalty_redeemed.amount)}</span></div>}
          <div className="flex justify-between font-bold border-t mt-2 pt-2"><span>Totale</span><span>{fmt.eur(order.total)}</span></div>
          {order.loyalty_earned > 0 && <p className="text-xs text-[hsl(var(--success))] mt-2 text-center">Hai guadagnato {order.loyalty_earned} punti fedeltà! 🎉</p>}
        </div>

        <ProximityOffers orderToken={token} />

        <p className="text-center text-xs text-muted-foreground mt-6 flex items-center justify-center gap-1.5" data-testid="live-status">
          <span className={`inline-block w-2 h-2 rounded-full ${live ? "bg-[hsl(var(--success))]" : "bg-amber-400 animate-pulse"}`} />
          {live ? "Aggiornamento in tempo reale" : "Riconnessione in corso…"} · powered by Pickfood
        </p>
      </div>
    </div>
  );
}
